import { ChangeDetectionStrategy, Component, input, output, computed, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Field } from '../../../../types/fields';
import { FieldTypeIconComponent } from '../../../../components/field-type-icon.component';
import { DefaultValueInputComponent } from '../../../../components/default-value-input/default-value-input.component';
import { DocumentTemplateField } from '../../../../types/document-template';
import { OcrPrimeStore } from '../../../../store/ocr-prime.store';

@Component({
  selector: 'app-object-field-editor',
  standalone: true,
  imports: [FormsModule, MatIconModule, MatTooltipModule, FieldTypeIconComponent, DefaultValueInputComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (isObjectList()) {
      <!-- Object-list: array of object items -->
      <div class="space-y-3">
        <div class="flex items-center justify-between">
          <span class="text-xs font-semibold text-zinc-500 dark:text-zinc-400 uppercase">Items ({{ items().length }})</span>
          <button
            type="button"
            (click)="addItem()"
            class="inline-flex items-center gap-1 rounded-md bg-blue-50 dark:bg-blue-900/30 px-2 py-1 text-xs font-medium text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-800/50 border border-blue-200 dark:border-blue-700 transition-colors"
          >
            <mat-icon class="!text-sm !w-3.5 !h-3.5 !leading-[14px]">add</mat-icon>
            Add Item
          </button>
        </div>

        @for (item of items(); track $index; let idx = $index) {
          <div class="rounded-lg border border-zinc-200 dark:border-zinc-700 bg-zinc-50/50 dark:bg-zinc-800/50 overflow-hidden">
            <div class="flex items-center justify-between px-3 py-2 bg-zinc-100/50 dark:bg-zinc-700/50 border-b border-zinc-200 dark:border-zinc-700">
              <span class="text-xs font-semibold text-zinc-600 dark:text-zinc-400">Item {{ idx + 1 }}</span>
              <button
                type="button"
                (click)="removeItem(idx)"
                class="inline-flex items-center justify-center rounded-md p-1 text-zinc-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
                matTooltip="Remove item"
              >
                <mat-icon class="!text-sm !w-3.5 !h-3.5 !leading-[14px]">close</mat-icon>
              </button>
            </div>
            <div class="p-3 space-y-3">
              @for (subField of resolvedSubFields(); track subField.id) {
                <div class="space-y-1">
                  <div class="flex items-center gap-1.5">
                    <app-field-type-icon [type]="subField.type" size="sm" />
                    <label class="text-xs font-medium text-zinc-700 dark:text-zinc-300">{{ subField.name }}</label>
                    @if (subField.required) {
                      <span class="text-red-500 text-xs">*</span>
                    }
                  </div>
                  @if (subField.description) {
                    <p class="text-[10px] text-zinc-500 dark:text-zinc-400">{{ subField.description }}</p>
                  }
                  <app-default-value-input
                    [fieldType]="subField.type"
                    [ngModel]="getItemSubFieldValue(idx, subField.id)"
                    (ngModelChange)="setItemSubFieldValue(idx, subField.id, $event)"
                  />
                </div>
              }
            </div>
          </div>
        }

        @if (items().length === 0) {
          <div class="text-center py-4 text-zinc-400 dark:text-zinc-500 text-xs">
            No items yet. Click "Add Item" to start.
          </div>
        }
      </div>
    } @else {
      <!-- Object: single object with sub-fields -->
      <div class="space-y-3">
        @for (subField of resolvedSubFields(); track subField.id) {
          <div class="space-y-1">
            <div class="flex items-center gap-1.5">
              <app-field-type-icon [type]="subField.type" size="sm" />
              <label class="text-xs font-medium text-zinc-700 dark:text-zinc-300">{{ subField.name }}</label>
              @if (subField.required) {
                <span class="text-red-500 text-xs">*</span>
              }
            </div>
            @if (subField.description) {
              <p class="text-[10px] text-zinc-500 dark:text-zinc-400">{{ subField.description }}</p>
            }
            <app-default-value-input
              [fieldType]="subField.type"
              [ngModel]="getSubFieldValue(subField.id)"
              (ngModelChange)="setSubFieldValue(subField.id, $event)"
            />
          </div>
        }
      </div>
    }
  `
})
export class ObjectFieldEditorComponent {
  fieldDefinition = input.required<Field>();
  value = input<DocumentTemplateField['exampleValue']>(null);
  valueChange = output<DocumentTemplateField['exampleValue']>();

  private readonly store = inject(OcrPrimeStore);

  isObjectList = computed(() => this.fieldDefinition().type === 'object-list');

  resolvedSubFields = computed<Field[]>(() => {
    const def = this.fieldDefinition();
    if (!def.fieldIds || def.fieldIds.length === 0) return [];
    const fieldsMap = this.store.fieldsMap();
    return def.fieldIds
      .map(id => fieldsMap[id])
      .filter((f): f is Field => !!f);
  });

  // For object-list: parsed items array
  items = computed<Array<{ [fieldId: string]: DocumentTemplateField }>>(() => {
    const val = this.value();
    if (this.isObjectList() && Array.isArray(val)) {
      return val as Array<{ [fieldId: string]: DocumentTemplateField }>;
    }
    return [];
  });

  // Object: get a sub-field's exampleValue
  getSubFieldValue(subFieldId: string): any {
    const val = this.value();
    if (val && typeof val === 'object' && !Array.isArray(val)) {
      const obj = val as { [key: string]: DocumentTemplateField };
      return obj[subFieldId]?.exampleValue ?? null;
    }
    return null;
  }

  // Object: set a sub-field's exampleValue
  setSubFieldValue(subFieldId: string, newVal: any): void {
    const current = this.value();
    let obj: { [key: string]: DocumentTemplateField } = {};
    if (current && typeof current === 'object' && !Array.isArray(current)) {
      obj = { ...(current as { [key: string]: DocumentTemplateField }) };
    }
    // Initialize all sub-fields if empty
    for (const sf of this.resolvedSubFields()) {
      if (!obj[sf.id]) {
        obj[sf.id] = { exampleValue: null };
      }
    }
    obj[subFieldId] = { ...obj[subFieldId], exampleValue: newVal };
    this.valueChange.emit(obj);
  }

  // Object-list: get a sub-field value at item index
  getItemSubFieldValue(itemIndex: number, subFieldId: string): any {
    const list = this.items();
    if (itemIndex < list.length) {
      return list[itemIndex]?.[subFieldId]?.exampleValue ?? null;
    }
    return null;
  }

  // Object-list: set a sub-field value at item index
  setItemSubFieldValue(itemIndex: number, subFieldId: string, newVal: any): void {
    const list = [...this.items()];
    if (itemIndex < list.length) {
      const item = { ...list[itemIndex] };
      item[subFieldId] = { ...(item[subFieldId] || {}), exampleValue: newVal };
      list[itemIndex] = item;
      this.valueChange.emit(list);
    }
  }

  addItem(): void {
    const list = [...this.items()];
    const newItem: { [fieldId: string]: DocumentTemplateField } = {};
    for (const sf of this.resolvedSubFields()) {
      newItem[sf.id] = { exampleValue: null };
    }
    list.push(newItem);
    this.valueChange.emit(list);
  }

  removeItem(index: number): void {
    const list = [...this.items()];
    list.splice(index, 1);
    this.valueChange.emit(list);
  }
}
